import { ClientEnvironment } from '@polyai/common/constants/global.constants';

import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPatch, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  CreateProjectReq,
  ProjectDetails,
  ProjectRes,
  ProjectResourceRes,
  ProjectsFeaturesRes,
  ProjectsMetricsRes,
  ProjectsRes,
  ProjectWithTokenExceeded,
  UpdateProject,
} from './types';

export const getProjects = (accountId: string) => {
  return doGet<ProjectsRes>(
    generateNextPath(ENDPOINTS.PROJECTS, {
      accountId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const getProjectsMetrics = (accountId: string) => {
  return doGet<ProjectsMetricsRes>(
    generateNextPath(ENDPOINTS.PROJECTS_METRICS, {
      accountId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const getProjectsFeatures = (accountId: string) => {
  return doGet<ProjectsFeaturesRes>(
    generateNextPath(ENDPOINTS.PROJECTS_FEATURES, {
      accountId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const getProject = (accountId: string, projectId: string) => {
  return doGet<ProjectWithTokenExceeded>(
    generateNextPath(ENDPOINTS.PROJECT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const deleteProject = ({
  accountId,
  projectId,
}: {
  accountId: string;
  projectId: string;
}) => {
  return doDelete<{ success: boolean }>(
    generateNextPath(ENDPOINTS.PROJECT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const duplicateProject = ({
  accountId,
  projectId,
  newProjectId,
  newProjectName,
  useSourcerer = false,
}: {
  accountId: string;
  projectId: string;
  newProjectId?: string;
  newProjectName?: string;
  useSourcerer?: boolean;
}) => {
  const body =
    newProjectId || newProjectName
      ? {
          ...(newProjectId && { new_project_id: newProjectId }),
          ...(newProjectName && { new_project_name: newProjectName }),
        }
      : undefined;
  return doPost<{ success: boolean }>(
    generateNextPath(ENDPOINTS.DUPLICATE_PROJECT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    body,
    {
      version: useSourcerer ? 'v1' : 'v2',
      prefix: useSourcerer ? 'sourcerer' : undefined,
    },
  );
};

export const updateProject = ({
  accountId,
  projectId,
  update,
}: {
  accountId: string;
  projectId: string;
  update: UpdateProject;
}) => {
  return doPatch<ProjectRes>(
    generateNextPath(ENDPOINTS.PROJECT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    update,
    { version: 'v2' },
  );
};

export const createProject = ({
  accountId,
  body,
  useSourcerer = false,
}: {
  accountId: string;
  body: CreateProjectReq;
  useSourcerer?: boolean;
}) => {
  return doPost<ProjectDetails>(
    generateNextPath(ENDPOINTS.PROJECTS, {
      accountId,
    }),
    useSourcerer ? LOGGER_TYPE.AGENT_STREAM : LOGGER_TYPE.JUPITER,
    body,
    {
      version: useSourcerer ? 'v1' : 'v2',
      prefix: useSourcerer ? 'sourcerer' : undefined,
    },
  );
};

export const publishDraft = ({
  accountId,
  projectId,
  deploymentMessage,
}: {
  accountId: string;
  projectId: string;
  deploymentMessage: string;
}) => {
  return doPost<ProjectRes>(
    generateNextPath(ENDPOINTS.PUBLISH, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      deployment_message: deploymentMessage,
      client_env: ClientEnvironment.SANDBOX,
    },
    { version: 'v2' },
  );
};

export const deleteDraft = ({
  accountId,
  projectId,
}: {
  accountId: string;
  projectId: string;
}) => {
  return doDelete<ProjectRes>(
    generateNextPath(ENDPOINTS.DRAFT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const getEnabledFeatures = (accountId: string, projectId?: string) => {
  return doGet<ProjectResourceRes>(
    generateNextPath(ENDPOINTS.PROJECT_FEATURES, {
      accountId,
      projectId: projectId!,
    }),
    LOGGER_TYPE.PROJECT,
    // { version: 'v2' },
  );
};

export const exportProject = ({
  accountId,
  projectId,
  useSourcerer = false,
}: {
  accountId: string;
  projectId: string;
  useSourcerer: boolean;
}) => {
  return doGet<{ data: Blob; headers: Headers }>(
    generateNextPath(ENDPOINTS.EXPORT_PROJECT, {
      accountId,
      projectId,
    }),
    useSourcerer ? LOGGER_TYPE.AGENT_STREAM : LOGGER_TYPE.JUPITER,
    {
      version: useSourcerer ? 'v1' : 'v2',
      downloadingFile: true,
      returnStatusCode: true,
      prefix: useSourcerer ? 'sourcerer' : undefined,
    },
  );
};

export const importProject = ({
  accountId,
  file,
  newProjectId,
  newProjectName,
  useSourcerer = false,
}: {
  accountId: string;
  file: File;
  newProjectId?: string;
  newProjectName?: string;
  useSourcerer: boolean;
}) => {
  const url = generateNextPath(ENDPOINTS.IMPORT_PROJECT, { accountId });
  const bodyFormData = new FormData();
  const blob = new Blob([file], { type: 'application/octet-stream' });
  bodyFormData.append('project', blob);
  if (newProjectId) {
    bodyFormData.append('new_project_id', newProjectId);
  }
  if (newProjectName) {
    bodyFormData.append('new_project_name', newProjectName);
  }
  return doPost<ProjectRes>(
    url,
    useSourcerer ? LOGGER_TYPE.AGENT_STREAM : LOGGER_TYPE.JUPITER,
    bodyFormData,
    {
      postingFile: true,
      version: useSourcerer ? 'v1' : 'v2',
      parseResponseToJSON: true,
      prefix: useSourcerer ? 'sourcerer' : undefined,
    },
  );
};
